/**
 * Hiring rule: an applicant may submit only ONE job application per calendar
 * month. Once they have applied for a posting, every other posting stays
 * locked until the 1st of the following month.
 *
 * All comparisons use UTC because `applications.applied_at` is written with
 * `Date#toISOString()`. Keeping the browser and the API on the same UTC basis
 * means both agree on which month a given application belongs to, so the UI
 * never shows an open form for something the API is going to reject.
 */

/** Machine-readable code returned by the API so the UI can branch on it. */
export const MONTHLY_APPLICATION_LIMIT_CODE = 'MONTHLY_APPLICATION_LIMIT';

export type MonthWindow = {
  /** Inclusive lower bound (ISO string) — 1st of the month, 00:00:00.000Z. */
  startIso: string;
  /** Exclusive upper bound (ISO string) — 1st of the next month. */
  endIso: string;
};

/**
 * Returns the half-open UTC window `[start, end)` covering the calendar month
 * that contains `date`. Use it directly as `.gte()` / `.lt()` bounds.
 */
export function getUtcMonthWindow(date: Date = new Date()): MonthWindow {
  const start = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0)
  );
  const end = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1, 0, 0, 0, 0)
  );

  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

/** True when `value` falls inside the same UTC calendar month as `reference`. */
export function isSameUtcMonth(
  value: string | Date | null | undefined,
  reference: Date = new Date()
): boolean {
  if (!value) return false;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return false;

  return (
    date.getUTCFullYear() === reference.getUTCFullYear() &&
    date.getUTCMonth() === reference.getUTCMonth()
  );
}

/**
 * Formats a date in UTC. Used server-side (and by the client for consistency)
 * so the "you may apply again from …" message never shifts with the visitor's
 * timezone.
 */
export function formatDateUtc(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }
): string {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(
    date
  );
}
